import Link from 'next/link';
import { getSignupHref } from '@/lib/signup';
import type { SignupIntent } from '@/lib/types';

type ButtonVariant = 'primary' | 'secondary';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  className?: string;
  children: React.ReactNode;
}

interface ButtonLinkProps extends ButtonBaseProps {
  href: string;
  signup?: never;
}

/** Opens the shared registration dialog; the href is the signup.ts fallback. */
interface ButtonSignupProps extends ButtonBaseProps {
  signup: SignupIntent;
  href?: never;
}

interface ButtonActionProps extends ButtonBaseProps {
  onClick: () => void;
  href?: never;
  signup?: never;
  disabled?: boolean;
}

type ButtonProps = ButtonLinkProps | ButtonSignupProps | ButtonActionProps;

const isExternal = (href: string) => /^(https?:|mailto:|tel:)/.test(href);

/** Approved canideal.org button (`.cid-button` in src/styles/cid/shell.css). */
export function Button(props: ButtonProps) {
  const { variant = 'primary', className = '', children } = props;
  const classes = `cid-button ${variant} ${className}`.trim();

  if ('onClick' in props && props.onClick) {
    return (
      <button type="button" className={classes} onClick={props.onClick} disabled={props.disabled}>
        {children}
      </button>
    );
  }

  if ('signup' in props && props.signup) {
    return (
      <a className={classes} href={getSignupHref(props.signup)} data-cid-signup={props.signup}>
        {children}
      </a>
    );
  }

  const href = (props as ButtonLinkProps).href;
  if (isExternal(href)) {
    return (
      <a
        className={classes}
        href={href}
        {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <Link className={classes} href={href}>
      {children}
    </Link>
  );
}
